import { GetCustomerCartQuery, VirtualProductCartItemInput } from '../../graphql/magento'
import { Maybe } from '../../utils/types'
import { userCategories, UserCategory } from '../../utils/userClaims'
import { defaultProductLabelDesignFeeSKU } from './config'
import { AUTO_SHIP_DISCOUNT_RATE } from './item-control-options'
import { CartItemVirtualProduct } from './state/types'
import { CartItem, CartItems } from './types'

export enum OrderType {
  SingleOrder = 1,
  AutoShip = 2,
}

/** True when a form/cart order-type value (arriving as a number or numeric string) is auto-ship. */
export const isAutoShipOrderType = (value: string | number | undefined): boolean =>
  Number(value) === OrderType.AutoShip

/**
 * Whether a surface may state the flat Subscribe & Save rate to this shopper before Magento has
 * applied one — the PDP card's Save badge, its discounted estimate, the cart drawer's Subscribe
 * chip. Patients and practitioners both qualify (sc-131111).
 *
 * For a practitioner the flat 5% is a deliberate promise across every rewards level, not just the
 * levels where it beats their VIP discount. The two don't stack, so Magento applies the greater of
 * the two: at VIP Level 1 (0%) that is the 5%, and from Level 2 up (VIP >= 5%) the VIP rate. We show
 * `Save 5%` regardless because hiding it above Level 1 was judged more confusing than the mismatch
 * (2026-09-09). The estimate stays conservative — it never shows a price below what they pay — and
 * surfaces that read the discount Magento actually applied (`matchesAutoShipDiscount`) still reflect
 * the real rate. Employees, other professionals, and an absent claim stay out of the promise.
 *
 * Separate reasoning from `defaultOrderTypeFor`, which preselects Subscribe for patients only.
 */
export const promisesAutoShipDiscountTo = (userCategory: UserCategory | undefined): boolean =>
  userCategory === userCategories.patient || userCategory === userCategories.practitioner

/**
 * Which purchase type the PDP preselects. Subscribe & Save is the default for patients only
 * (sc-131111): practitioners and other professional categories could place unintended auto-ship
 * orders on behalf of patients, and an absent or unrecognized category claim gives no basis to
 * opt the shopper into a subscription.
 */
export const defaultOrderTypeFor = ({
  showSubscribeRedesign,
  userCategory,
}: {
  showSubscribeRedesign: boolean
  userCategory: UserCategory | undefined
}): OrderType =>
  showSubscribeRedesign && userCategory === userCategories.patient
    ? OrderType.AutoShip
    : OrderType.SingleOrder

/**
 * True when a Magento line discount is plausibly the Subscribe & Save discount, i.e. within
 * rounding of `unitPrice * quantity * AUTO_SHIP_DISCOUNT_RATE`. `total_item_discount` covers every
 * line-apportioned discount (coupons, cart rules, VIP tiers), so displays that describe the amount
 * as the Subscribe & Save discount must not trust it alone.
 *
 * The tolerance is the tighter of two bounds:
 * - a cent per unit, because the backend's rounding mode is not observable from here: row-level
 *   rounding errs by at most half a cent, but per-unit rounding multiplied by quantity errs by up
 *   to half a cent per unit
 * - half a percentage point of the row, so the rounding allowance cannot admit a neighbouring rate
 *   on a cheap line. A cent per unit is two percentage points of a 50c unit, which on its own would
 *   accept a 6% cart rule as Subscribe & Save. Written as a share of the row rather than of
 *   `expected`, so it stays half a point whatever `AUTO_SHIP_DISCOUNT_RATE` becomes
 *
 * The bounds cross at a $2 unit price. Below it the row is too small to tell 5% from the rates
 * either side of it, and the check fails closed — a missing strikethrough rather than a wrong
 * caption. A materially different discount (e.g. a 10% coupon) is off by a share of the row total,
 * far outside either bound at any price.
 */
export const matchesAutoShipDiscount = ({
  unitPrice,
  quantity,
  lineDiscount,
}: {
  unitPrice: number
  quantity: number
  lineDiscount: number
}): boolean => {
  const expected = unitPrice * quantity * AUTO_SHIP_DISCOUNT_RATE
  const tolerance = Math.min(0.01 * Math.max(1, quantity), unitPrice * quantity * 0.005)
  return lineDiscount > 0 && Math.abs(lineDiscount - expected) < tolerance
}

/**
 * Whether Subscribe & Save is what discounted this cart line: the line is on auto-ship *and* the
 * discount Magento applied matches the flat rate. Both halves are needed — `total_item_discount`
 * also covers coupons, cart rules and VIP tiers, so the amount alone would caption a Buy Once
 * line carrying an unrelated 5% rule as Subscribe & Save.
 *
 * Both drawer surfaces that report an applied discount — the struck-through unit price and the
 * Subscribe chip — resolve it here so they cannot disagree about whether the evidence is in hand.
 */
export const hasAppliedAutoShipDiscount = (item: CartItem): boolean =>
  isAutoShipOrderType(item.item_attributes?.dfh_order_type ?? undefined) &&
  matchesAutoShipDiscount({
    unitPrice: item.prices?.price?.value || 0,
    quantity: item.quantity,
    lineDiscount: item.prices?.total_item_discount?.value || 0,
  })

export const hasCartItems = (items: Maybe<CartItems>): boolean =>
  items?.some((item) => item != null) ?? false

export const stripHtmlTags = ({ text }: { text: string }): string => text.replace(/<(.|\n)*?>/g, '')

export const itemDisplayNameVariantInfo = ({
  itemAttributes,
}: {
  itemAttributes: CartItem['item_attributes']
}): string => {
  if (itemAttributes?.flavor_size) {
    return `, ${itemAttributes.flavor_size}`
  }

  if (itemAttributes?.flavor) {
    return `, ${itemAttributes.flavor}`
  }

  if (itemAttributes?.dosage) {
    return `, ${itemAttributes.dosage}`
  }

  return ''
}

export const shouldShowCaliforniaProp65Asterisks = ({
  item,
  shippingAddress,
}: {
  item: CartItem
  shippingAddress?: GetCustomerCartQuery['customerCart']['shipping_addresses'][0]
}): boolean =>
  !!shippingAddress &&
  // prop_sixty_five value is null, 0, or 1
  !!item.item_attributes?.prop_sixty_five &&
  shippingAddress.country.code === 'US' &&
  shippingAddress.region?.code === 'CA'

export const shouldShowCaliforniaProp65Warning = ({
  items,
  shippingAddress,
}: {
  items: CartItems
  shippingAddress?: GetCustomerCartQuery['customerCart']['shipping_addresses'][0]
}): boolean =>
  !!shippingAddress &&
  shippingAddress.country.code === 'US' &&
  shippingAddress.region?.code === 'CA' &&
  hasCaliforniaProp65Items(items)

// The per-item warning keys on `prop65_message`, not `prop_sixty_five`: progesterone products carry
// `P65-PG` with `prop_sixty_five: 0`.
export const prop65MessageCodes = ['Prop-65', 'P65-CJ', 'P65-PG'] as const
export type Prop65MessageCode = (typeof prop65MessageCodes)[number]

export const isProp65MessageCode = (value: unknown): value is Prop65MessageCode =>
  prop65MessageCodes.some((code) => code === value)

export const prop65MessageCodeOf = (item: CartItems[number]): Prop65MessageCode | null => {
  const code = item?.item_attributes?.prop65_message
  return isProp65MessageCode(code) ? code : null
}

export const hasCaliforniaProp65Items = (items: CartItems): boolean =>
  items.some((item) => prop65MessageCodeOf(item) !== null)

export interface Prop65WarningGroup {
  code: Prop65MessageCode
  productNames: string[]
}

/** One entry per warning present in the cart, naming the products it applies to. */
export const prop65WarningGroups = (items: CartItems): Prop65WarningGroup[] =>
  prop65MessageCodes
    .map((code) => ({
      code,
      productNames: [
        ...new Set(
          items.flatMap((item) =>
            item?.product.name && prop65MessageCodeOf(item) === code ? [item.product.name] : [],
          ),
        ),
      ],
    }))
    .filter((group) => group.productNames.length > 0)

export const cartItemDisplayName = ({
  item,
  shippingAddress = null,
}: {
  item: CartItem
  shippingAddress?: GetCustomerCartQuery['customerCart']['shipping_addresses'][0]
}): string => {
  return `${shouldShowCaliforniaProp65Asterisks({ item, shippingAddress }) ? '** ' : ''}${
    item.product.name
  }`
}

/** The bare product name for accessible names; the display name may carry Prop 65 asterisks. */
export const cartItemAccessibleName = ({ item }: { item: CartItem }): string =>
  item.product.name ?? ''

export function isProductInStock(
  itemVariant: Pick<NonNullable<CartItem['product']>, 'stock_status'>,
): boolean {
  return itemVariant.stock_status === 'IN_STOCK'
}

// Some of the product attributes are HTML containing paragraphs with only
// a non-breaking space inside in an apparent attempt to handle spacing. Because
// this is only done for some of the products it can cause really large gaps
// between paragraphs on some of the PDPs but not others so to make things
// consistent these 'empty' p tags must be removed.
export const removeEmptyParagraphs = ({ htmlString }: { htmlString: string }): string =>
  htmlString.replace(/<p>\s*<\/p>/gi, '')

export const truncatedNonHtmlText = ({
  text,
  wordCount = 5,
}: {
  text: string
  wordCount?: number
}): string => stripHtmlTags({ text }).split(' ').slice(0, wordCount).join(' ')

export const parseItemIdAsNumber = (item: CartItem): number => {
  const itemId = Number(item.id)
  if (Number.isNaN(itemId)) {
    throw new Error(`Unable to parse item id "${item.id}" as number`)
  }
  return itemId
}

// Excludes items dependent on other items' inclusion in the cart from cart total quantity
export const totalQuantityExcludingDependents = (
  cartItems: Maybe<CartItems>,
  totalCount: number,
): number =>
  Math.max(
    0,
    totalCount -
      (cartItems ?? []).reduce(
        (acc: number, item) => (isItemLabelDesignFee(item) ? acc + item.quantity : acc),
        0,
      ),
  )

export const totalItemsDisplay = (quantity: number): string =>
  `${quantity} Item${quantity === 1 ? '' : 's'}`

export const isVirtualCartItem = (item: Maybe<CartItem>): item is CartItemVirtualProduct =>
  !!item && item.__typename === 'VirtualCartItem'

export const isItemLabelDesignFee = (item: Maybe<CartItem>): item is CartItemVirtualProduct =>
  isVirtualCartItem(item) && item.product.sku === defaultProductLabelDesignFeeSKU

type CartItemSelectedCustomizableOption = NonNullable<
  NonNullable<CartItemVirtualProduct['customizable_options']>[number]
>

const isSelectedCustomizableOption = (
  option: unknown,
): option is CartItemSelectedCustomizableOption =>
  option !== null &&
  typeof option === 'object' &&
  '__typename' in option &&
  option.__typename === 'SelectedCustomizableOption'

export const labelDesignItemValues = (
  item: CartItemVirtualProduct,
): { label: string; value: string }[] => {
  const customizableOptions = item.customizable_options?.filter(isSelectedCustomizableOption)
  return customizableOptions
    ? customizableOptions
        .filter((customOption) => customOption.label !== 'Product')
        .sort((a, b) => (a.sort_order < b.sort_order ? -1 : 1))
        .reduce((acc: { label: string; value: string }[], customOption) => {
          acc.push({
            label: customOption.label,
            value: customOption.values[0]?.value || '',
          })
          return acc
        }, [])
    : []
}

/**
 * Search virtual cart item's customizable options for option with label `Product` and return value (if any)
 */
export const productOptionValueFromCartItem = (
  item: CartItemVirtualProduct,
): string | undefined => {
  const productOption = item.customizable_options?.find((option) => option?.label === 'Product')
  return productOption?.values?.[0]?.value
}

/**
 * Search cart items for corresponding label design item
 */
export const correspondingLabelDesignItem = (item: CartItem, items: CartItems): Maybe<CartItem> =>
  items?.find(
    (cartItem) =>
      isItemLabelDesignFee(cartItem) &&
      isVirtualCartItem(cartItem) &&
      cartItem.customizable_options?.find(
        (customOption) => !!customOption?.values.find((value) => value?.value === item.product.sku),
      ),
  )

export interface CartItemAndLabelDesignFee {
  item: CartItem
  designFeeItem: CartItemVirtualProduct | undefined
}

function isCartItemAndLabelDesignFee(
  partial: Partial<CartItemAndLabelDesignFee>,
): partial is CartItemAndLabelDesignFee {
  return !!partial.item
}

/**
 * Collect items with paired label design fee (for PL)
 */
export const collectItemAndDesignFeePairs = (
  items: CartItems,
): Map<string, CartItemAndLabelDesignFee> => {
  const partials = items.reduce(
    (acc: Map<string, Partial<CartItemAndLabelDesignFee>>, cartItem) => {
      if (isVirtualCartItem(cartItem) && isItemLabelDesignFee(cartItem)) {
        const productSkuValue = cartItem.customizable_options?.find(
          (customOption) => customOption?.label === 'Product',
        )?.values?.[0]?.value
        if (!productSkuValue) {
          return acc
        }
        const existingPair = acc.get(productSkuValue)
        if (existingPair) {
          existingPair.designFeeItem = cartItem
        } else {
          acc.set(productSkuValue, {
            item: undefined,
            designFeeItem: cartItem,
          })
        }
      } else {
        const sku = cartItem?.product?.sku
        // shouldn't happen
        if (!sku) {
          return acc
        }
        const existingPair = acc.get(sku)
        if (existingPair) {
          existingPair.item = cartItem
        } else {
          acc.set(sku, {
            item: cartItem,
            designFeeItem: undefined,
          })
        }
      }
      return acc
    },
    new Map<string, Partial<CartItemAndLabelDesignFee>>(),
  )

  // filter out any partials without a primary cart item
  const result = new Map<string, CartItemAndLabelDesignFee>()
  for (const [sku, partial] of partials.entries()) {
    if (isCartItemAndLabelDesignFee(partial)) {
      result.set(sku, partial)
    }
  }
  return result
}

export const labelDesignFeeCartItemInput = ({
  forSku,
  desiredName,
  additionalDetails,
}: {
  forSku: string
  desiredName: string
  additionalDetails?: string
}): VirtualProductCartItemInput => ({
  data: {
    sku: defaultProductLabelDesignFeeSKU,
    quantity: 1,
  },
  customizable_options: [
    {
      id: 1,
      value_string: forSku,
    },
    {
      id: 2,
      value_string: desiredName,
    },
    ...(additionalDetails
      ? [
          {
            id: 3,
            value_string: additionalDetails,
          },
        ]
      : []),
  ],
})
